<template>
  <aha-modal @hide="hide()" :title="modalTitle">
    <div :inner-html.prop="modalContent" />
    <template #action>
      <aha-antd-button
        variant="secondary"
        @click="hide()"
      >
        <span>{{ $i18nPresenter.t("Cancel") }}</span>
      </aha-antd-button>
      <aha-antd-button
        v-aha-emit-action
        v-blur-on-click
        name="delete-folder"
        variant="danger"
        :disabled="isDeletingFolder"
        @click="handleDeleteFolder()"
        :submitting="isDeletingFolder"
        is-loading-inline
        has-margin-left
      >
        {{ $i18nPresenter.t("Delete") }}
      </aha-antd-button>
    </template>
  </aha-modal>
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'

// api
import FolderApi from '@/api/folder'

// constants
import eventCategories from '@/constant/eventCategories'

// utils
import utils from '@/util'

export default {
  props: {
    folder: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isDeletingFolder: false,
      sumOfSubFolders: 0,
      deletedFolderId: [],
      deletedPresentationId: [],
    }
  },
  computed: {
    ...mapState({
      folders: (state) => state.folders,
      currentFolderDepth: (state) => state.currentFolderDepth,
    }),
    ...mapGetters(['getPresenterLocale']),
    modalTitle() {
      if (this.getPresenterLocale === 'en') {
        return `Delete ${this.folder.folderName} folder`
      }
      return this.$i18nPresenter.t('Remove confirmation')
    },
    hasPresentationsInside() {
      return this.folder.countPresentationRecursive && this.folder.countPresentationRecursive > 0
    },
    modalContent() {
      return `${this.$i18nPresenter.t("You're about to permanently delete")} <span class="font-bold">${utils.sanitize(this.folder.folderName)}</span> ${this.$i18nPresenter.t('folder and its')} <span class="font-bold">${this.folder.countPresentationRecursive} ${this.folder.countPresentationRecursive > 1 ? this.$i18nPresenter.t('presentations') : this.$i18nPresenter.t('presentation')}</span>.`
    },
  },
  methods: {
    ...mapActions([
      'setFoldersAction',
      'setErrorOnInternalServerAction',
    ]),
    handleAfterCallAPIDeleteFolder() {
      utils.showToastSuccess(`${this.$i18nPresenter.t('Deleted folder')} "${utils.sanitize(this.folder.folderName)}"`)
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Folder Kebab Menu > Delete Folder > Succeeded', null, null, {
        parentFolderId: this.selectedFolderId,
        folderDepth: this.currentFolderDepth || 0,
        deletedFolderId: this.deletedFolderId,
        deletedPresentationId: this.deletedPresentationId,
      })
      const index = this.folders.findIndex((folder) => {
        return folder.folderId === this.folder.folderId
      })
      this.folders.splice(index, 1)
      this.setFoldersAction(this.folders)
    },
    async handleDeleteFolder() {
      this.isDeletingFolder = true
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Folder Kebab Menu > Delete Folder > Submit', null, null, {
        parentFolderId: this.selectedFolderId,
        folderDepth: this.currentFolderDepth || 0,
      })
      try {
        const { deletedFolderIds, deletedPresentationIds } = await FolderApi.removeFolderById(this.folder.folderId)
        this.deletedFolderId = deletedFolderIds.map((folder) => folder.folderId)
        this.deletedPresentationId = deletedPresentationIds.map((presentation) => presentation.id)
        this.handleAfterCallAPIDeleteFolder()
      } catch (error) {
        Sentry.captureException(error)
        utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Folder Kebab Menu > Delete Folder > Failed', null, null, {
          parentFolderId: this.selectedFolderId,
          folderDepth: this.currentFolderDepth || 0,
          deletedFolderId: this.deletedFolderId,
          deletedPresentationId: this.deletedPresentationId,
        })
        this.setErrorOnInternalServerAction(`Cannot delete folder ${this.folder.folderName}`)
      } finally {
        this.isDeletingFolder = false
        this.hide()
      }
    },
    hide() {
      this.$emit('hide')
    },
  },
}
</script>
